import React, { useState } from 'react';

const LicensePanel = ({ currentUser, licenseKey, isLicenseActive, onActivateLicense }) => {
  const [inputKey, setInputKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');

  const DEMO_LICENSES = {
    'MTA-PRO-2025-DEMO': { plan: 'Pro', expires: '2025-12-31', domains: ['unlimited'] },
    'MTA-STARTER-2025': { plan: 'Starter', expires: '2025-06-30', domains: ['example.com'] },
    'MTA-ENTERPRISE-2025': { plan: 'Enterprise', expires: '2026-12-31', domains: ['unlimited'] }
  };

  const GradientButton = ({ onClick, children, secondary, destructive, disabled, fullWidth }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="transition-all duration-300"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '12px 24px',
        background: disabled ? '#d1d5db' : destructive ? '#ef4444' : secondary ? '#6b7280' : 'linear-gradient(135deg, #06ADED, #38BFEF)',
        border: 'none',
        borderRadius: '8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: disabled ? 'none' : destructive ? '0 4px 12px rgba(239, 68, 68, 0.3)' : secondary ? '0 2px 6px rgba(107, 114, 128, 0.2)' : '0 4px 12px rgba(6, 173, 237, 0.3)',
        color: 'white',
        fontSize: '14px',
        fontWeight: '600',
        opacity: disabled ? 0.6 : 1,
        width: fullWidth ? '100%' : 'auto'
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = destructive ? '0 6px 16px rgba(239, 68, 68, 0.4)' : secondary ? '0 4px 10px rgba(107, 114, 128, 0.3)' : '0 6px 16px rgba(6, 173, 237, 0.4)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = destructive ? '0 4px 12px rgba(239, 68, 68, 0.3)' : secondary ? '0 2px 6px rgba(107, 114, 128, 0.2)' : '0 4px 12px rgba(6, 173, 237, 0.3)';
        }
      }}
    >
      <span>{children}</span>
    </button>
  );

  const validateLicense = async (key) => {
    setIsValidating(true);
    setValidationError('');

    await new Promise(resolve => setTimeout(resolve, 1500));

    if (DEMO_LICENSES[key]) {
      onActivateLicense(key);
      setIsValidating(false);
      return;
    }

    setValidationError('Invalid license key. Try one of the demo keys below.');
    setIsValidating(false);
  };

  const handleActivate = () => {
    if (!inputKey.trim()) {
      setValidationError('Please enter a license key');
      return;
    }
    validateLicense(inputKey.trim());
  };

  const getLicenseInfo = () => {
    if (!licenseKey || !DEMO_LICENSES[licenseKey]) {
      return null;
    }
    return DEMO_LICENSES[licenseKey];
  };

  const licenseInfo = getLicenseInfo();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">License Management</h1>
          <p className="text-gray-500">
            Activate your license to enable your widgets on external websites
          </p>
        </div>

        <div className={`rounded-xl border-2 p-6 mb-6 shadow-sm ${
          isLicenseActive 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {isLicenseActive ? 'License Active' : 'No Active License'}
              </h2>
              <p className={`text-sm mb-4 ${isLicenseActive ? 'text-green-700' : 'text-red-700'}`}>
                {isLicenseActive 
                  ? 'Your license is active and your widgets are working' 
                  : 'Activate a license to enable your widgets'}
              </p>
              {isLicenseActive && licenseInfo && (
                <div className="space-y-2">
                  <div className="text-sm text-gray-700">
                    <span className="font-semibold">Plan:</span> {licenseInfo.plan}
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-semibold">Expires:</span> {licenseInfo.expires}
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-semibold">License Key:</span>{' '}
                    <code className="bg-white bg-opacity-70 px-2 py-1 rounded text-xs font-mono">
                      {licenseKey}
                    </code>
                  </div>
                </div>
              )}
            </div>
            {isLicenseActive && (
              <button
                onClick={() => onActivateLicense(null)}
                className="px-4 py-2 bg-white border-2 border-green-300 text-green-700 rounded-lg hover:bg-green-50 text-sm font-semibold transition-all"
              >
                Deactivate
              </button>
            )}
          </div>
        </div>

        {!isLicenseActive && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activate Your License</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter License Key
                </label>
                <input
                  type="text"
                  value={inputKey}
                  onChange={(e) => {
                    setInputKey(e.target.value);
                    setValidationError('');
                  }}
                  placeholder="MTA-PRO-2025-XXXX"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mono transition-all"
                  style={{ outline: 'none' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#06ADED';
                    e.target.style.boxShadow = '0 0 0 3px rgba(6, 173, 237, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                  disabled={isValidating}
                />
                {validationError && (
                  <p className="mt-2 text-sm text-red-600 font-medium">{validationError}</p>
                )}
              </div>

              <GradientButton
                onClick={handleActivate}
                disabled={isValidating || !inputKey.trim()}
                fullWidth
              >
                {isValidating ? 'Validating...' : 'Activate'}
              </GradientButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LicensePanel